import React, { useEffect, useRef } from 'react';
import {
    GA_SEARCH_CATEGORY,
    SEARCH_EVENT_NAVIGATION,
    SEARCH_PROMOTED_LIST_NAVIGATION,
} from '../../../../constants/analytics';
import {
    SearchResultEventCard,
    SearchResultEventCardProps,
} from '../SearchResultEventCard';

interface SearchResultPanelEventContentCardProps
    extends Omit<SearchResultEventCardProps, 'heapLocationString'> {
    isRaised: boolean;
    scrollId?: string;
    setHoverEventTarget: Function;
    onTrackEventClickInternal: Function;
    trackGAEvent: Function;
    position: number;
}

export const SearchResultPanelContentEventCard: React.FunctionComponent<SearchResultPanelEventContentCardProps> =
    React.memo(
        ({
            scrollId,
            position,
            setHoverEventTarget,
            onTrackEventClickInternal,
            trackGAEvent,
            event,
            ...otherProps
        }) => {
            const { handleTrackClick, handleTrackClickInternal } =
                useTrackingHandlers(event.isPromoted, {
                    onTrackEventClickInternal,
                    trackGAEvent,
                });
            const cardRef = useScrollIntoViewEffects(scrollId, event.id);
            const heapLocationString = event.isPromoted
                ? 'Search - Promoted'
                : 'Search - Primary Results';

            return (
                <div
                    data-testid="search-event"
                    onMouseEnter={() => setHoverEventTarget(event.id)}
                    onFocus={() => setHoverEventTarget(event.id)}
                    onMouseLeave={() => setHoverEventTarget(null)}
                    onBlur={() => setHoverEventTarget(null)}
                    role="presentation"
                    ref={cardRef}
                    key={`result-panel-content-card-${event.id}-${event.isPromoted}`}
                >
                    <SearchResultEventCard
                        trackClick={handleTrackClick}
                        trackClickInternal={handleTrackClickInternal}
                        event={event}
                        heapLocationString={heapLocationString}
                        position={position}
                        {...otherProps}
                    />
                </div>
            );
        },
        areEqual,
    );

function areEqual(
    prevProps: SearchResultPanelEventContentCardProps,
    nextProps: SearchResultPanelEventContentCardProps,
) {
    //return true if no re-render
    //return false if should re-render
    if (
        prevProps.event.dedup !== nextProps.event.dedup ||
        prevProps.event.urgencySignals !== nextProps.event.urgencySignals ||
        prevProps.isRaised !== nextProps.isRaised ||
        (nextProps.scrollId === nextProps.event.id && !prevProps.scrollId) ||
        prevProps.event.organizerName !== nextProps.event.organizerName ||
        prevProps.event.imageUrl !== nextProps.event.imageUrl ||
        prevProps.event.name !== nextProps.event.name ||
        prevProps.isExpansionsLoading !== nextProps.isExpansionsLoading
    ) {
        return false;
    }

    return true;
}

function useScrollIntoViewEffects(scrollId: string | undefined, id: string) {
    const cardRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (scrollId === id && cardRef.current) {
            cardRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, [scrollId, id]);

    return cardRef;
}

export function useTrackingHandlers(
    isPromoted: boolean | undefined,
    {
        trackGAEvent,
        onTrackEventClickInternal,
    }: Pick<
        SearchResultPanelEventContentCardProps,
        'trackGAEvent' | 'onTrackEventClickInternal'
    >,
) {
    const handleTrackClick = (
        position: number,
        eventId: string,
        action: string,
        organizerFollowerCount: number,
    ) =>
        trackGAEvent({
            category: GA_SEARCH_CATEGORY,
            label: eventId,
            action: isPromoted ? 'ViewEventPromotedResult' : action,
            dimensions: {
                eventId,
            },
            metrics: {
                searchRank: position,
                organizerFollowerCount,
            },
        });

    const handleTrackClickInternal = (
        position: number,
        eventId: string,
        ticketsBy: string,
    ) => {
        const trackingData = {
            clickRank: position,
            navigation: isPromoted
                ? SEARCH_PROMOTED_LIST_NAVIGATION
                : SEARCH_EVENT_NAVIGATION,
            itemId: eventId,
        };
        onTrackEventClickInternal(trackingData, ticketsBy);
    };

    return {
        handleTrackClick,
        handleTrackClickInternal,
    };
}
